import { z } from "zod";

/** Max length of a single chat message body, enforced client- and server-side. */
export const CHAT_MESSAGE_MAX_LENGTH = 500;

/**
 * Max size of a raw inbound WebSocket frame, in UTF-8 bytes, rejected
 * before JSON.parse ever runs. Generous enough for a max-length message
 * (500 chars, up to 4 bytes/char) plus JSON envelope overhead, but still a
 * small, deliberate ceiling — chat frames have no business being large.
 */
export const CHAT_WS_FRAME_MAX_BYTES = 4096;

export const chatMessageSchema = z.object({
  id: z.string(),
  roomId: z.string(),
  userId: z.string(),
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  body: z.string().min(1).max(CHAT_MESSAGE_MAX_LENGTH),
  createdAt: z.string(),
});

export type ChatMessageDTO = z.infer<typeof chatMessageSchema>;

/**
 * Client -> DO WebSocket message envelope. `body` is trimmed before the
 * length check, so a whitespace-only message (e.g. "   ") is rejected the
 * same way an empty one is, instead of being accepted and rendered as a
 * blank bubble.
 */
export const chatClientMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("send"),
    body: z
      .string()
      .max(CHAT_MESSAGE_MAX_LENGTH)
      .transform((value) => value.trim())
      .pipe(z.string().min(1, "Message cannot be empty.")),
  }),
  z.object({ type: z.literal("ping") }),
]);

export type ChatClientMessage = z.infer<typeof chatClientMessageSchema>;

/** DO -> client WebSocket message envelope. */
export type ChatServerMessage =
  | { type: "message"; message: ChatMessageDTO }
  | { type: "presence"; count: number }
  | { type: "history"; messages: ChatMessageDTO[] }
  | { type: "error"; code: string; message: string }
  | { type: "pong" };

/** POST /api/v1/chat/ws-ticket response — a short-lived, single-purpose WebSocket connect ticket. */
export const wsTicketResponseSchema = z.object({
  ticket: z.string(),
  expiresAt: z.number().int(),
  roomId: z.string(),
});

export type WsTicketResponse = z.infer<typeof wsTicketResponseSchema>;
